/**
 * PurchaseOrdersPage — Issue #331.
 *
 * 采购订单列表：状态过滤 + 创建草稿 + 状态流转动作（提交/审批/取消）。
 * 收货走详情页，避免在列表批量收货误操作。
 */
import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import Decimal from 'decimal.js';
import { useAuth } from '@/hooks/useAuth';
import {
  hasPermission,
  PURCHASE_ORDER_PERMISSIONS,
} from '@/lib/procurementPermissions';
import {
  usePurchaseOrderList,
  usePurchaseOrderActions,
  type CreatePurchaseOrderDto,
  type CreatePurchaseOrderItemDto,
  type PurchaseOrderSummaryDto,
} from '@/hooks/usePurchaseOrder';
import { SupplierPicker } from '@/components/business/SupplierPicker';
import type { SupplierSummaryDto } from '@/hooks/useSupplier';

const STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  submitted: '已提交',
  approved: '已批准',
  partially_received: '部分收货',
  received: '已收货',
  closed: '已关闭',
  cancelled: '已取消',
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-blue-100 text-blue-800',
  partially_received: 'bg-orange-100 text-orange-800',
  received: 'bg-green-100 text-green-800',
  closed: 'bg-gray-200 text-gray-500',
  cancelled: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
      }`}
    >
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function fmtCny(amount: string): string {
  return `¥${new Decimal(amount).toFixed(2)}`;
}

interface CreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

function CreateDialog({ open, onClose, onCreated }: CreateDialogProps) {
  const { create, loading, error } = usePurchaseOrderActions();
  // Issue #426: supplier is selected from a fuzzy picker. The form only
  // submits the selected supplier's real internal id; the picker's typed
  // search keyword is never echoed into the create body.
  const [selectedSupplier, setSelectedSupplier] =
    useState<SupplierSummaryDto | null>(null);
  const [supplierValidationError, setSupplierValidationError] = useState<
    string | null
  >(null);
  const [currency, setCurrency] = useState('CNY');
  const [expected, setExpected] = useState('');
  const [items, setItems] = useState<CreatePurchaseOrderItemDto[]>([
    { materialName: '', unit: 'piece', quantity: '1', unitPrice: '0' },
  ]);

  if (!open) return null;

  const total = items
    .reduce((acc, it) => {
      const q = new Decimal(it.quantity || '0');
      const p = new Decimal(it.unitPrice || '0');
      return acc.plus(q.mul(p));
    }, new Decimal(0))
    .toFixed(2);

  function updateItem(idx: number, patch: Partial<CreatePurchaseOrderItemDto>) {
    setItems((cur) => cur.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function addItem() {
    setItems((cur) => [
      ...cur,
      { materialName: '', unit: 'piece', quantity: '1', unitPrice: '0' },
    ]);
  }
  function removeItem(idx: number) {
    setItems((cur) => cur.filter((_, i) => i !== idx));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selectedSupplier) {
      setSupplierValidationError(
        '请先在上方搜索框中选择一个供应商，再创建采购订单。',
      );
      return;
    }
    setSupplierValidationError(null);
    const dto: CreatePurchaseOrderDto = {
      supplierId: selectedSupplier.id,
      currency,
      expectedArrivalAt: expected || undefined,
      items,
    };
    const ok = await create(dto);
    if (ok) {
      onCreated();
      onClose();
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-2xl rounded-lg bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="mb-4 text-lg font-semibold">新建采购订单草稿</h2>
        {error && (
          <div className="mb-3 rounded bg-red-50 px-3 py-2 text-sm text-red-700">
            {error.message}
          </div>
        )}
        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="space-y-3"
        >
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-3 md:col-span-1">
              <label
                htmlFor="purchase-order-supplier-picker"
                className="block text-sm font-medium text-gray-700"
              >
                供应商
              </label>
              <SupplierPicker
                id="purchase-order-supplier-picker"
                required
                value={selectedSupplier}
                onChange={(next) => {
                  setSelectedSupplier(next);
                  if (next) setSupplierValidationError(null);
                }}
                testId="purchase-order-supplier-picker"
              />
              {supplierValidationError ? (
                <p
                  className="mt-1 text-xs text-red-600"
                  data-testid="purchase-order-supplier-error"
                >
                  {supplierValidationError}
                </p>
              ) : null}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">币种</label>
              <input
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">预计到货</label>
              <input
                type="date"
                className="mt-1 w-full rounded border border-gray-300 px-3 py-1.5 text-sm"
                value={expected}
                onChange={(e) => setExpected(e.target.value)}
              />
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">条目</h3>
              <button
                type="button"
                onClick={addItem}
                className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
              >
                + 添加
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div
                  key={idx}
                  className="space-y-1 rounded border border-gray-200 p-2"
                >
                  <div className="grid grid-cols-12 items-center gap-2">
                    <input
                      required
                      placeholder="物料名称"
                      className="col-span-3 rounded border border-gray-300 px-2 py-1 text-sm"
                      value={it.materialName}
                      onChange={(e) =>
                        updateItem(idx, { materialName: e.target.value })
                      }
                    />
                    <input
                      placeholder="规格"
                      className="col-span-3 rounded border border-gray-300 px-2 py-1 text-sm"
                      value={it.specification ?? ''}
                      onChange={(e) =>
                        updateItem(idx, {
                          specification: e.target.value || undefined,
                        })
                      }
                    />
                    <input
                      placeholder="consumable ID（可选）"
                      className="col-span-2 rounded border border-gray-300 px-2 py-1 text-sm font-mono"
                      value={it.consumableId ?? ''}
                      onChange={(e) =>
                        updateItem(idx, {
                          consumableId: e.target.value || undefined,
                        })
                      }
                    />
                    <input
                      required
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]+(\.[0-9]+)?"
                      placeholder="数量"
                      className="col-span-1 rounded border border-gray-300 px-2 py-1 text-sm"
                      value={it.quantity}
                      onChange={(e) =>
                        updateItem(idx, { quantity: e.target.value })
                      }
                    />
                    <input
                      required
                      type="text"
                      inputMode="decimal"
                      pattern="[0-9]+(\.[0-9]+)?"
                      placeholder="单价"
                      className="col-span-2 rounded border border-gray-300 px-2 py-1 text-sm"
                      value={it.unitPrice}
                      onChange={(e) =>
                        updateItem(idx, { unitPrice: e.target.value })
                      }
                    />
                    <button
                      type="button"
                      onClick={() => removeItem(idx)}
                      disabled={items.length === 1}
                      className="col-span-1 rounded border border-gray-300 bg-white px-1 py-0.5 text-xs text-gray-600 disabled:opacity-50"
                    >
                      删除
                    </button>
                  </div>
                  <input
                    placeholder="服务目录 ID（可选，须为 approved+enabled）"
                    className="w-full rounded border border-gray-300 px-2 py-1 text-xs font-mono text-gray-700"
                    value={it.serviceCatalogId ?? ''}
                    onChange={(e) =>
                      updateItem(idx, {
                        serviceCatalogId: e.target.value || undefined,
                      })
                    }
                  />
                </div>
              ))}
            </div>
            <div className="mt-2 text-right text-sm text-gray-700">
              小计：<span className="font-semibold">¥{total}</span>
            </div>
          </div>

          <div className="mt-4 flex justify-end gap-2">
            <button
              type="button"
              className="rounded border border-gray-300 px-4 py-1.5 text-sm"
              onClick={onClose}
            >
              取消
            </button>
            <button
              type="submit"
              disabled={loading || !selectedSupplier}
              data-testid="purchase-order-create-submit"
              className="rounded bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {loading ? '提交中…' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function PurchaseOrdersPage() {
  const auth = useAuth();
  const [, navigate] = useLocation();
  const permissions = useMemo(() => auth.permissions ?? [], [auth.permissions]);
  const canRead = hasPermission(permissions, PURCHASE_ORDER_PERMISSIONS.READ);
  const canCreate = hasPermission(
    permissions,
    PURCHASE_ORDER_PERMISSIONS.CREATE,
  );
  const canUpdate = hasPermission(
    permissions,
    PURCHASE_ORDER_PERMISSIONS.UPDATE,
  );
  const canApprove = hasPermission(
    permissions,
    PURCHASE_ORDER_PERMISSIONS.APPROVE,
  );

  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  const { data, loading, error, fetchList } = usePurchaseOrderList();
  const {
    submit,
    approve,
    cancel,
    loading: actionLoading,
  } = usePurchaseOrderActions();

  const refresh = () =>
    fetchList({ status: statusFilter || undefined, search: search || undefined });

  useEffect(() => {
    if (canRead) {
      void refresh();
    }
  }, [canRead]);

  if (!canRead) {
    return (
      <div className="p-6 text-sm text-gray-600">您没有查看采购订单的权限。</div>
    );
  }

  async function onSubmit(row: PurchaseOrderSummaryDto) {
    const ok = await submit(row.id, { expectedVersion: row.version });
    if (ok) void refresh();
  }
  async function onApprove(row: PurchaseOrderSummaryDto) {
    const ok = await approve(row.id, { expectedVersion: row.version });
    if (ok) void refresh();
  }
  async function onCancel(row: PurchaseOrderSummaryDto) {
    const reason = window.prompt('请输入取消原因：');
    if (!reason) return;
    const ok = await cancel(row.id, {
      expectedVersion: row.version,
      reason,
    });
    if (ok) void refresh();
  }

  return (
    <div className="space-y-4 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold">采购订单</h1>
        {canCreate && (
          <button
            onClick={() => setCreateOpen(true)}
            className="rounded bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
          >
            新建采购订单
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3 rounded border border-gray-200 bg-gray-50 p-3">
        <div>
          <label className="block text-xs font-medium text-gray-600">状态</label>
          <select
            className="mt-1 rounded border border-gray-300 px-2 py-1 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">全部</option>
            {Object.entries(STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600">搜索</label>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
            placeholder="订单号"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          onClick={() => void refresh()}
          className="rounded border border-gray-300 bg-white px-3 py-1 text-sm"
        >
          {loading ? '加载中…' : '查询'}
        </button>
      </div>

      {error && (
        <div className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      )}

      <div className="overflow-x-auto rounded border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left">订单号</th>
              <th className="px-3 py-2 text-left">供应商</th>
              <th className="px-3 py-2 text-right">金额</th>
              <th className="px-3 py-2 text-left">状态</th>
              <th className="px-3 py-2 text-left">预计到货</th>
              <th className="px-3 py-2 text-left">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {(data?.rows ?? []).map((row) => (
              <tr key={row.id}>
                <td className="px-3 py-2 font-mono text-xs">
                  <button
                    onClick={() => navigate(`/procurement/purchase-orders/${row.id}`)}
                    className="text-blue-600 hover:underline"
                  >
                    {row.orderNo}
                  </button>
                </td>
                <td className="px-3 py-2 font-mono text-xs">{row.supplierId}</td>
                <td className="px-3 py-2 text-right">
                  {fmtCny(row.totalAmount)}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={row.status} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {row.expectedArrivalAt
                    ? new Date(row.expectedArrivalAt).toLocaleDateString()
                    : '—'}
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {row.status === 'draft' && canUpdate && (
                      <button
                        disabled={actionLoading}
                        onClick={() => void onSubmit(row)}
                        className="rounded border border-gray-300 bg-white px-2 py-0.5 text-xs"
                      >
                        提交
                      </button>
                    )}
                    {row.status === 'submitted' && canApprove && (
                      <button
                        disabled={actionLoading}
                        onClick={() => void onApprove(row)}
                        className="rounded border border-green-300 bg-green-50 px-2 py-0.5 text-xs text-green-700"
                      >
                        批准
                      </button>
                    )}
                    {(row.status === 'approved' ||
                      row.status === 'partially_received') && (
                      <button
                        onClick={() =>
                          navigate(`/procurement/purchase-orders/${row.id}`)
                        }
                        className="rounded border border-blue-300 bg-blue-50 px-2 py-0.5 text-xs text-blue-700"
                      >
                        收货
                      </button>
                    )}
                    {(row.status === 'draft' ||
                      row.status === 'submitted' ||
                      row.status === 'approved') &&
                      canUpdate && (
                        <button
                          disabled={actionLoading}
                          onClick={() => void onCancel(row)}
                          className="rounded border border-red-300 bg-red-50 px-2 py-0.5 text-xs text-red-700"
                        >
                          取消
                        </button>
                      )}
                  </div>
                </td>
              </tr>
            ))}
            {(data?.rows ?? []).length === 0 && !loading && (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                  暂无采购订单
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <CreateDialog
        key={createOpen ? 'open' : 'closed'}
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void refresh()}
      />
    </div>
  );
}
